import type { ListBuilderConfiguration } from '../../types';
import { activeCustomersList } from './lists/active-customers-list';
import { atRiskList } from './lists/at-risk-list';
import { contactDueList } from './lists/contact-due-list';
import { defaultClientsAllList } from './lists/default-clients-all';
import { defaultClientsNearMeList } from './lists/default-clients-near-me';
import { defaultClientsOpenOrdersList } from './lists/default-clients-open-orders';
import { dormantCustomersList } from './lists/dormant-customers-list';
import { myClientsList } from './lists/my-clients-list';
import { newLeadsList } from './lists/new-leads-list';
import { overdueList } from './lists/overdue-list';
import { prospectingList } from './lists/prospecting-list';
import { scheduledTodayList } from './lists/scheduled-today-list';
import { suggestedSalesList } from './lists/suggested-sales-list';
import { unassignedClientsList } from './lists/unassigned-clients-list';

/**
 * Client list configuration
 * Includes multiple filtered views for managing clients and leads
 */
export const CLIENT_CONFIG: ListBuilderConfiguration = {
	objectType: 'client',
	lists: [
		defaultClientsAllList,
		scheduledTodayList,
		overdueList,
		contactDueList,
		suggestedSalesList,
		atRiskList,
		defaultClientsOpenOrdersList,
		defaultClientsNearMeList,
		myClientsList,
		newLeadsList,
		prospectingList,
		activeCustomersList,
		dormantCustomersList,
		unassignedClientsList,
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'clientSearch',
			'clientCompany',
			'salespeople',
			'alternateRep',
			'lifecycleStageName',
			'businessTypes',
			'leadOrCustomer',
			'salesStatus',
			'callStatus',
			'geography',
			'nearMe',
			'revenue90Days',
			'orderSalesStatus',
			'groups',
			'clientLists',
			'excludeClientLists',
			'clientDeletedRecords',
		],
		// PD-14228: the Ownership multi-select filter is intentionally NOT listed
		// here, so it is OFF by default. Its `listKeys: ['client']` registration
		// puts it in the List Configuration modal's available pool, where users
		// can drag it in when they want it.
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'legalName', fieldLabel: 'Legal Name' },
				{ fieldName: 'email', fieldLabel: 'Email' },
				{ fieldName: 'phone', fieldLabel: 'Phone' },
				{ fieldName: 'address1', fieldLabel: 'Address 1' },
				{ fieldName: 'city', fieldLabel: 'City' },
				{ fieldName: 'state', fieldLabel: 'State' },
				{ fieldName: 'zip', fieldLabel: 'Zip' },
				{ fieldName: 'lastCallDate', fieldLabel: 'Last Call Date' },
				{ fieldName: 'lastContactDate', fieldLabel: 'Last Contact Date' },
				{ fieldName: 'lastNoteText', fieldLabel: 'Last Note' },
				{ fieldName: 'salesStatus', fieldLabel: 'erpStatus' },
				{ fieldName: 'leadOrCustomer', fieldLabel: 'Lead / Customer' },
				{ fieldName: 'creditBalance', fieldLabel: 'Credit Balance' },
				{ fieldName: 'creditLimit', fieldLabel: 'Credit Limit' },
				{ fieldName: 'hasCreditHold', fieldLabel: 'Credit Hold' },
				{
					fieldName: 'averageInvoiceRevenue',
					fieldLabel: 'Average Invoice Revenue',
				},
				{ fieldName: 'invoiceFrequency', fieldLabel: 'Invoice Frequency' },
				{
					fieldName: 'forecastedReorderDate',
					fieldLabel: 'Forecasted Reorder Date',
				},
				{ fieldName: 'revenueTrend90', fieldLabel: 'Revenue Trend 90' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
				{ fieldName: 'latitude', fieldLabel: 'Latitude' },
				{ fieldName: 'longitude', fieldLabel: 'Longitude' },
				{ fieldName: 'googlePlaceId', fieldLabel: 'Google Place ID' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name',
			name: 'Name',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'name',
					objectTable: 'client',
				},
			],
			description: 'Sort clients by name',
		},
		{
			id: 'recent-contact',
			name: 'Last Contact',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastCallDate',
					objectTable: 'client',
				},
			],
			description: 'Sort clients by last contact date',
		},
		{
			id: 'location',
			name: 'By Location',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'state',
					objectTable: 'client',
				},
				{
					direction: 'ASC',
					fieldName: 'city',
					objectTable: 'client',
				},
			],
			description: 'Sort by state then city',
		},
		{
			id: 'next-activity',
			name: 'Next Activity',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'nextActivityTime',
					objectTable: 'task_data',
				},
			],
			description: 'Sort by next activity time',
		},
		{
			id: 'forecast',
			name: 'Forecast',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'forecastedReorderDate',
					objectTable: 'client',
					nullsLast: true,
				},
			],
			description: 'Sort by forecasted reorder date',
		},
		{
			id: 'last-assigned-date-desc',
			name: 'Last Assigned Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastAssignedDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last assigned date (newest first)',
		},
		{
			id: 'last-assigned-date-asc',
			name: 'Last Assigned Date (Oldest First)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastAssignedDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last assigned date (oldest first)',
		},
		{
			id: 'last-call-date-desc',
			name: 'Last Call Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastCallDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last call date (newest first)',
		},
		{
			id: 'last-call-date-asc',
			name: 'Last Call Date (Oldest First)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastCallDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last call date (oldest first)',
		},
		{
			id: 'last-invoice-date-desc',
			name: 'Last Invoice Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastInvoiceDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last invoice date (newest first)',
		},
		{
			id: 'last-invoice-date-asc',
			name: 'Last Invoice Date (Oldest First)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastInvoiceDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last invoice date (oldest first)',
		},
		{
			id: 'rolling-revenue-30-desc',
			name: 'Revenue 30 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'rollingRevenue30',
					objectTable: 'client',
				},
			],
			description: 'Sort by 30-day rolling revenue (highest first)',
		},
		{
			id: 'rolling-revenue-30-asc',
			name: 'Revenue 30 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'rollingRevenue30',
					objectTable: 'client',
				},
			],
			description: 'Sort by 30-day rolling revenue (lowest first)',
		},
		{
			id: 'gp-90-days-desc',
			name: 'GP 90 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'rollingTotalGPAggregate90',
					objectTable: 'client',
				},
			],
			description: 'Sort by gross profit 90 days (highest first)',
		},
		{
			id: 'gp-90-days-asc',
			name: 'GP 90 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'rollingTotalGPAggregate90',
					objectTable: 'client',
				},
			],
			description: 'Sort by gross profit 90 days (lowest first)',
		},
		{
			id: 'gp-trend-90-days-desc',
			name: 'GP Trend 90 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'rollingTotalGPMargin90',
					objectTable: 'client',
				},
			],
			description: 'Sort by gross profit margin trend 90 days (highest first)',
		},
		{
			id: 'gp-trend-90-days-asc',
			name: 'GP Trend 90 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'rollingTotalGPMargin90',
					objectTable: 'client',
				},
			],
			description: 'Sort by gross profit margin trend 90 days (lowest first)',
		},
		{
			id: 'revenue-90-days-desc',
			name: 'Revenue 90 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'rollingRevenue90',
					objectTable: 'client',
				},
			],
			description: 'Sort by revenue 90 days (highest first)',
		},
		{
			id: 'revenue-90-days-asc',
			name: 'Revenue 90 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'rollingRevenue90',
					objectTable: 'client',
				},
			],
			description: 'Sort by revenue 90 days (lowest first)',
		},
		{
			id: 'revenue-trend-90-days-desc',
			name: 'Revenue Trend 90 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'minimumTrendDiff',
					objectTable: 'client',
				},
			],
			description: 'Sort by revenue trend 90 days (highest first)',
		},
		{
			id: 'revenue-trend-90-days-asc',
			name: 'Revenue Trend 90 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'minimumTrendDiff',
					objectTable: 'client',
				},
			],
			description: 'Sort by revenue trend 90 days (lowest first)',
		},
	],
};
